import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { CheckCircle, ArrowRight, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export type TipColor = 'purple' | 'green' | 'blue' | 'orange' | 'amber';

const COLOR_STYLES: Record<TipColor, { bg: string; icon: string; border: string }> = {
  purple: {
    bg: 'bg-purple-100',
    icon: 'text-purple-600',
    border: 'border-purple-200',
  },
  green: {
    bg: 'bg-green-100',
    icon: 'text-green-600',
    border: 'border-green-200',
  },
  blue: {
    bg: 'bg-blue-100',
    icon: 'text-blue-600',
    border: 'border-blue-200',
  },
  orange: {
    bg: 'bg-orange-100',
    icon: 'text-orange-600',
    border: 'border-orange-200',
  },
  amber: {
    bg: 'bg-amber-50',
    icon: 'text-amber-600',
    border: 'border-amber-200',
  },
};

export interface QuickTipCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  tips: string[];
  color: TipColor;
  actionLabel?: string;
  actionPath?: string;
  className?: string;
}

export function QuickTipCard({
  icon: Icon,
  title,
  description,
  tips,
  color,
  actionLabel,
  actionPath,
  className,
}: QuickTipCardProps) {
  const styles = COLOR_STYLES[color];

  return (
    <div
      className={cn(
        'bg-white rounded-lg border border-gray-200 p-5 flex flex-col',
        className
      )}
    >
      {/* Header with icon */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className={cn(
            'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
            styles.bg
          )}
        >
          <Icon className={cn('h-5 w-5', styles.icon)} />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600 mt-0.5">{description}</p>
        </div>
      </div>

      {/* Tips list */}
      <div className="space-y-2 flex-1">
        {tips.map((tip, index) => (
          <div key={index} className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700">{tip}</p>
          </div>
        ))}
      </div>

      {/* Action button */}
      {actionLabel && actionPath && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <Link href={actionPath}>
            <Button variant="outline" size="sm" className="w-full justify-between">
              {actionLabel}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
